import React, { FC, useCallback, useState } from 'react';

import { message, Modal } from 'antd';

import { t } from 'i18next';
import { MFA_ATTEMPTS } from '@/consts';
import AttemptsRemainWarning from '../../OpenViewer/AttemptsRemainWarning';
import MfaComp from '../../AppAuthenticator/MfaComp';
import { OpenOptions } from '@/config/openOptions.ts';
import { batchActionsNamespace } from '@/components/MyFiles/MyFilesHeader/ExtraContent/nameSpace.ts';
import { useStores } from '@/components/hooks';
import { amplifyRestApi, BASEURL, ENDPOINTS } from '@/api';

const MESSAGE_DURATION_SECONDS = 8;

const MFAModal: FC = () => {
    const { filesAccessStore, batchDownloadStore, filesListStore } = useStores();
    const {
        tryFetchAccessLink,
        tryStartBatchDownload,
    } = filesAccessStore;
    const {
        setMFARequiredFilesList,
        downloadsList,
        setIsMFAVisible,
        setIsMFAMounted,
        isMFAVisible,
    } = batchDownloadStore;
    const {
        filesToDisplay,
    } = filesListStore;

    const [localAttemptsCounter, setLocalAttemptsCounter] = useState<number>(MFA_ATTEMPTS);

    const closeMFA = useCallback((autoClosing: boolean = false): void => {
        if (!autoClosing) {
            const { MFARequiredFilesList, updateBatchDownloadStatusWithError } = batchDownloadStore;
            const filesData = filesToDisplay.filter((item) => MFARequiredFilesList.includes(item.file_id));
            filesData.forEach((item) => {
                updateBatchDownloadStatusWithError(item.file_id, item.filename);
            });
        }
        setMFARequiredFilesList([]);
        setIsMFAVisible(false);
        setIsMFAMounted(false);
    }, []);

    const onClickBatchLoading = async (mfaCode?: string): Promise<void> => {
        const { MFARequiredFilesList } = batchDownloadStore;
        const filesData = filesToDisplay.filter((item) => MFARequiredFilesList.includes(item.file_id));
        const anyFolders = filesData.some((item) => item.is_folder);
        const isDownloadingWorkspaceItself = filesData.length === 0 && MFARequiredFilesList.length !== 0;
        const isMultipleFiles = MFARequiredFilesList.length > 1;
        let isSucceed = false;

        try {
            // Always verify phone code first before attempting download
            const response = await amplifyRestApi.post(
                BASEURL.backend(),
                ENDPOINTS.verifyFileAccess(),
                {
                    body: { method: 'phone', attempt: mfaCode },
                },
            ) as { success?: boolean };

            if (!response?.success) {
                throw new Error('MFA phone verification failed');
            }

            // After verifyFileAccess succeeded, don't pass code to download calls
            if (!MFARequiredFilesList.length) {
                message.info(t(`${batchActionsNamespace}.noFiles`), MESSAGE_DURATION_SECONDS);
            } else if (isDownloadingWorkspaceItself || anyFolders || isMultipleFiles) {
                isSucceed = await tryStartBatchDownload(MFARequiredFilesList);
            } else {
                const fileId = MFARequiredFilesList[0];
                const downloadItem = downloadsList.find((item) => item.id === fileId);
                // Fallback to filesToDisplay if not found in downloadsList
                const fileData = downloadItem || filesToDisplay.find((item) => item.file_id === fileId);
                const filename = downloadItem?.name || (fileData as any)?.filename;

                if (!filename) {
                    throw new Error('Could not find filename for file');
                }

                isSucceed = await tryFetchAccessLink({
                    fileId,
                    filename,
                    accessType: OpenOptions.download,
                });
            }
            if (isSucceed) {
                setLocalAttemptsCounter(MFA_ATTEMPTS); // Reset for next time
                closeMFA(true);
            }
        } catch (e) {
            // Handle failed verification - decrement attempts and show error
            if (localAttemptsCounter <= 1) {
                // No more attempts left
                message.error(t(`${batchActionsNamespace}.checkCodeLimit`), MESSAGE_DURATION_SECONDS);
                setLocalAttemptsCounter(MFA_ATTEMPTS); // Reset for next time
                closeMFA(true);
            } else {
                // Still have attempts left - keep modal open
                setLocalAttemptsCounter((prev) => prev - 1);
                message.error(t('recipientPage.errors.notAuthorisedCode'), MESSAGE_DURATION_SECONDS);
            }
        }
    };

    const onMfaClick = async (mfaCode: string): Promise<void> => {
        if (mfaCode) {
            await onClickBatchLoading(mfaCode);
        }
    };

    return (
        <Modal
            data-testid="mfaModal_container"
            open={isMFAVisible}
            centered
            footer={null}
            onCancel={() => closeMFA(false)}
            maskClosable={false}
            destroyOnClose
        >
            <>
                {localAttemptsCounter < MFA_ATTEMPTS && (
                    <AttemptsRemainWarning attemptsCounter={localAttemptsCounter} />
                )}
                <MfaComp onMfaClick={onMfaClick} />
            </>
        </Modal>
    );
};

export default MFAModal;
